import { supabase } from '../lib/supabase';

// ============================================================
// ROLE DEFINITIONS & PERMISSIONS
// ============================================================

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  AGENT: 'agent',
  CHAUFFEUR: 'chauffeur',
  AGENT_ABIDJAN: 'agent_abidjan',
};

export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  agent: 'Agent',
  chauffeur: 'Chauffeur',
  agent_abidjan: 'Agent Abidjan',
};

export const ROLE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  agent: 'bg-green-100 text-green-800',
  chauffeur: 'bg-orange-100 text-orange-800',
  agent_abidjan: 'bg-teal-100 text-teal-800',
};

// Routes accessible by role
export const ROLE_PERMISSIONS = {
  super_admin: {
    allowedRoutes: ['*'], // All routes
    deniedRoutes: [],
  },
  admin: {
    allowedRoutes: [
      '/admin-dashboard',
      '/admin-shipments-dashboard',
      '/shipment-detail-management',
      '/admin-shipment-detail-view',
      '/create-new-shipment',
      '/admin-clients',
      '/admin-client-detail',
      '/customer-management-system',
      '/admin-containers-dashboard',
      '/container-management-dashboard',
      '/admin-container-detail',
      '/container-detail-management',
      '/employee-management-dashboard',
      '/pickup-task-planning-system',
      '/fatou-ai-reservation-capture-system',
      '/admin-fatou-reservations-dashboard',
      '/automated-reservation-confirmation-system',
      '/professional-clients-management-dashboard',
      '/detailed-professional-client-profile',
      '/professional-orders-processing-center',
      '/abidjan-container-distribution-dashboard',
      '/abidjan-container-detail-management',
      '/abidjan-delivery-incidents-management',
      '/unified-sales-management',
      '/barrel-deposit-scheduling-system',
      '/barrel-customer-orders-management',
      '/unified-inventory-management',
      '/unified-stock-analytics',
      '/unified-replenishment-center',
      '/carton-suppliers-management',
      '/truck-loading-management',
      '/barrel-customer-management-dashboard',
      '/barrel-deposit-planning-system',
      '/admin-notifications',
      '/admin-fatou-reservations-dashboard',
    ],
    deniedRoutes: [
      '/admin-users-permissions',
      '/security-journal',
      '/whats-app-configuration-management',
      '/universal-api-endpoint-configuration',
      '/n8n-webhook-monitoring-dashboard',
      '/seo-management-dashboard',
      '/technical-seo-performance-analyzer',
      '/keyword-research-optimization-center',
    ],
  },
  agent: {
    allowedRoutes: [
      '/admin-dashboard',
      '/admin-clients',
      '/admin-client-detail',
      '/customer-management-system',
      '/pickup-task-planning-system',
      '/admin-shipments-dashboard',
      '/shipment-detail-management',
      '/create-new-shipment',
    ],
    deniedRoutes: ['*'],
  },
  chauffeur: {
    allowedRoutes: [
      '/driver-dashboard',
      '/driver-personal-dashboard',
      '/driver-pickup-detail-with-integrated-signature',
      '/driver-route-sheet-mobile-signature',
    ],
    deniedRoutes: ['*'],
  },
  agent_abidjan: {
    allowedRoutes: [
      '/dashboard-agent-abidjan',
      '/abidjan-container-distribution-dashboard',
      '/abidjan-container-detail-management',
      '/abidjan-delivery-incidents-management',
    ],
    deniedRoutes: ['*'],
  },
};

// ============================================================
// USER PROFILE OPERATIONS
// ============================================================

export const getUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('id', userId)
      ?.single();
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

export const getAllUsers = async () => {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.order('created_at', { ascending: false });
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

export const createUser = async ({ email, password, fullName, role, createdBy }) => {
  try {
    // Step 1: Check if user already exists in user_profiles
    const { data: existingProfile } = await supabase
      ?.from('user_profiles')
      ?.select('id, email, full_name, role')
      ?.eq('email', email)
      ?.maybeSingle();

    if (existingProfile) {
      // User already exists in user_profiles — just update name/role if needed
      const { data: updatedProfile, error: updateError } = await supabase
        ?.from('user_profiles')
        ?.update({
          full_name: fullName || existingProfile?.full_name,
          role,
          is_active: true,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', existingProfile?.id)
        ?.select()
        ?.single();

      // Sync to admin_users
      await supabase?.from('admin_users')?.upsert({
        email,
        full_name: fullName || existingProfile?.full_name,
        role,
        is_active: true,
      });

      return { data: updatedProfile, error: updateError };
    }

    // Step 2: Check if user exists in admin_users but not in user_profiles
    const { data: existingAdminUser } = await supabase
      ?.from('admin_users')
      ?.select('id, email, full_name, role')
      ?.eq('email', email)
      ?.maybeSingle();

    // Step 3: Try to create auth user via admin API
    let authUserId = null;
    let authError = null;

    try {
      const { data: authData, error: adminAuthError } = await supabase?.auth?.admin?.createUser({
        email,
        password,
        email_confirm: true,
        user_metadata: { full_name: fullName, role },
      });

      if (adminAuthError) {
        // If user already exists in auth (email already registered)
        if (
          adminAuthError?.message?.includes('already been registered') ||
          adminAuthError?.message?.includes('already exists') ||
          adminAuthError?.message?.includes('duplicate') ||
          adminAuthError?.status === 422
        ) {
          // User exists in auth but not in user_profiles — we need to find their auth ID
          // We'll upsert the profile without the auth ID link (use email as key)
          authUserId = null;
          authError = null; // Not a fatal error
        } else {
          authError = adminAuthError;
        }
      } else {
        authUserId = authData?.user?.id;
      }
    } catch (e) {
      // admin.createUser not available client-side — try signUp as fallback
      try {
        const { data: signUpData, error: signUpError } = await supabase?.auth?.signUp({
          email,
          password,
          options: {
            data: { full_name: fullName, role },
          },
        });

        if (signUpError) {
          if (
            signUpError?.message?.includes('already registered') ||
            signUpError?.message?.includes('already exists')
          ) {
            authUserId = null; // Exists in auth, proceed to upsert profile
            authError = null;
          } else {
            authError = signUpError;
          }
        } else {
          authUserId = signUpData?.user?.id;
        }
      } catch (signUpEx) {
        authError = signUpEx;
      }
    }

    if (authError) return { data: null, error: authError };

    // Step 4: Upsert user_profiles
    const profilePayload = {
      email,
      full_name: fullName,
      role,
      is_active: true,
      created_by: createdBy,
    };

    if (authUserId) {
      profilePayload.id = authUserId;
    }

    let profileData, profileError;

    if (authUserId) {
      const result = await supabase
        ?.from('user_profiles')
        ?.upsert(profilePayload)
        ?.select()
        ?.single();
      profileData = result?.data;
      profileError = result?.error;
    } else {
      // No auth ID available — upsert by email using update or insert
      const { data: insertData, error: insertError } = await supabase
        ?.from('user_profiles')
        ?.upsert({ ...profilePayload }, { onConflict: 'email', ignoreDuplicates: false })
        ?.select()
        ?.single();
      profileData = insertData;
      profileError = insertError;
    }

    // Step 5: Sync to admin_users
    await supabase?.from('admin_users')?.upsert({
      email,
      full_name: fullName,
      role,
      is_active: true,
      created_by: createdBy,
    });

    return { data: profileData, error: profileError };
  } catch (error) {
    return { data: null, error };
  }
};

export const updateUserRole = async (userId, newRole, updatedBy) => {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({ role: newRole, updated_at: new Date()?.toISOString() })
      ?.eq('id', userId)
      ?.select()
      ?.single();

    if (!error) {
      // Sync to admin_users
      await supabase?.from('admin_users')?.update({ role: newRole })?.eq('email', data?.email);
      
      // Log the event
      await logSecurityEvent({
        userId: updatedBy,
        eventType: 'role_changed',
        details: { target_user_id: userId, new_role: newRole },
      });
    }

    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

export const toggleUserActive = async (userId, isActive, updatedBy) => {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({ is_active: isActive, updated_at: new Date()?.toISOString() })
      ?.eq('id', userId)
      ?.select()
      ?.single();

    if (!error) {
      await supabase?.from('admin_users')?.update({ is_active: isActive })?.eq('email', data?.email);
      
      await logSecurityEvent({
        userId: updatedBy,
        eventType: isActive ? 'user_activated' : 'user_deactivated',
        details: { target_user_id: userId },
      });
    }

    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

export const deleteUser = async (userId, deletedBy) => {
  try {
    // Get user email first
    const { data: profile } = await supabase
      ?.from('user_profiles')
      ?.select('email, full_name')
      ?.eq('id', userId)
      ?.single();

    // Delete from user_profiles (cascades to auth)
    const { error } = await supabase?.from('user_profiles')?.delete()?.eq('id', userId);

    if (!error) {
      // Remove from admin_users
      await supabase?.from('admin_users')?.delete()?.eq('email', profile?.email);

      await logSecurityEvent({
        userId: deletedBy,
        eventType: 'user_deleted',
        details: { deleted_user_email: profile?.email, deleted_user_name: profile?.full_name },
      });
    }

    return { error };
  } catch (error) {
    return { error };
  }
};

export const updateUserProfile = async (userId, updates) => {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', userId)
      ?.select()
      ?.single();

    if (!error && updates?.full_name) {
      await supabase?.from('admin_users')?.update({ full_name: updates?.full_name })?.eq('email', data?.email);
    }

    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

// ============================================================
// SECURITY LOGGING
// ============================================================

export const logSecurityEvent = async ({ userId, userEmail, eventType, ipAddress, userAgent, details }) => {
  try {
    await supabase?.from('security_logs')?.insert({
      user_id: userId || null,
      user_email: userEmail || null,
      event_type: eventType,
      ip_address: ipAddress || null,
      user_agent: userAgent || navigator?.userAgent || null,
      details: details || {},
    });
  } catch (error) {
    console.warn('Security log error:', error);
  }
};

export const getSecurityLogs = async ({ limit = 100, eventType, userId } = {}) => {
  try {
    let query = supabase
      ?.from('security_logs')
      ?.select('*')
      ?.order('created_at', { ascending: false })
      ?.limit(limit);

    if (eventType) query = query?.eq('event_type', eventType);
    if (userId) query = query?.eq('user_id', userId);

    const { data, error } = await query;
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

// ============================================================
// PERMISSION CHECKS
// ============================================================

export const canAccessRoute = (role, pathname) => {
  if (!role) return false;
  if (role === ROLES?.SUPER_ADMIN) return true;

  const perms = ROLE_PERMISSIONS?.[role];
  if (!perms) return false;

  // Check denied routes first
  if (perms?.deniedRoutes?.includes('*')) {
    return perms?.allowedRoutes?.some((r) => pathname?.startsWith(r));
  }

  if (perms?.deniedRoutes?.some((r) => pathname?.startsWith(r))) return false;

  return perms?.allowedRoutes?.includes('*') || perms?.allowedRoutes?.some((r) => pathname?.startsWith(r));
};

export const isSuperAdmin = (role) => role === ROLES?.SUPER_ADMIN;
export const isAdminOrSuper = (role) => [ROLES?.SUPER_ADMIN, ROLES?.ADMIN]?.includes(role);
